<template>
  <q-modal :show="pinModalData.visible" :title="title" :class="$style.modal" @close="close">
    <folder-picker
      :class="$style.select"
      :selected-user-id="pinModalData.folderUserId"
      :account-users="accountUsers"
      @input="userSelected"
    />

    <comment-editor
      v-if="pinModalData.visible"
      ref="commentEditorRef"
      v-model="pinModalData.body"
      data-testid="pin-modal-editor"
      :class="$style.commentEditor"
      :placeholder="editing ? '' : $t('pins.modal.add_others_with')"
      :team-members="accountUsers"
      tall
      taglist-always-empty
      :template-type="TemplateType.Pin"
      @tags="setTags"
      @submit="save"
    />

    <div v-if="showPreview" :class="$style.pinReferenceContainer">
      <div :class="$style.divider" />
      <pin-reference-preview
        :reference-type="pinModalData.referenceType"
        :reference-value="pinModalData.referenceValue"
      />
    </div>

    <template #actions>
      <div :class="$style.actions">
        <how-pins-work class="shrink-0" />

        <div class="flex gap-3">
          <ui-button secondary @click="close">{{ $t('universal.cancel') }}</ui-button>
          <ui-button data-testid="pin-save" :disabled="savingDisabled" @click="save">{{ saveButtonText }}</ui-button>
        </div>
      </div>
    </template>
  </q-modal>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { UiButton } from '@klausapp/ui-kit'
import { PinReference_Type } from '@zendesk/zqa-services/pins'

import config, { embeddedRuntime } from '@/config'
import QModal from '@/components/QModal.vue'
import CommentEditor from '@/modules/shared/CommentEditor'
import { session } from '@/composables/useSession'
import { pinModalData, closePinModal } from '@/composables/usePinModal'
import { toast } from '@/components/toast'
import { bus } from '@/utils/bus'
import { searchUsers } from '@/api/users'
import type { AccountUser } from '@/modules/user-management/types'
import analytics from '@/utils/analytics'
import i18n from '@/i18n'
import { TemplateType } from '@/modules/shared/CommentEditor/templates/types'

import { addPinParticipant, createPin, editPin } from '../api'
import PinReferencePreview from './PinReferencePreview.vue'
import FolderPicker from './FolderPicker.vue'
import HowPinsWork from './HowPinsWork.vue'

interface Tag {
  tag: string
  userId: string
}

export default defineComponent({
  name: 'PinModal',
  components: {
    QModal,
    CommentEditor,
    PinReferencePreview,
    UiButton,
    FolderPicker,
    HowPinsWork,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()

    const commentEditorRef = ref<InstanceType<typeof CommentEditor>>()

    const editing = computed(() => !!pinModalData.value.pinId)
    const savingDisabled = computed(() => !pinModalData.value.folderUserId)
    const loading = ref(false)
    const tags = ref<Tag[]>([])
    const accountUsers = ref<AccountUser[]>([])

    const checkForNewParticipants = async (pinId: string) => {
      const newParticipants = tags.value.filter(({ userId }) => userId && userId !== String(session.user.id))
      if (newParticipants.length) {
        await Promise.all(newParticipants.map(({ userId }) => addPinParticipant(pinId, userId)))
        analytics.pinParticipantsAdded({
          count: newParticipants.length,
          addedVia: 'new_pin',
          isPinCreator: true,
        })
      }
    }

    const saveExistingPin = async () => {
      await editPin(pinModalData.value.pinId, {
        userId: pinModalData.value.folderUserId,
        body: pinModalData.value.body,
      })
      analytics.pinEdited({ type: pinModalData.value.referenceType, value: pinModalData.value.referenceValue })
      await checkForNewParticipants(pinModalData.value.pinId)
    }

    const saveNewPin = async () => {
      const { folderId, id: pinId } = await createPin({
        userId: pinModalData.value.folderUserId,
        body: pinModalData.value.body,
        reference: {
          type: pinModalData.value.referenceType,
          value: pinModalData.value.referenceValue,
        },
      })

      analytics.pinCreated(pinModalData.value)
      await checkForNewParticipants(pinId)

      toast({
        status: 'success',
        message: i18n.t('pins.modal.pin_created_with_action', {
          buttonStart: '<button type="button" class="toast__button">',
          buttonEnd: '</button>',
        }),
        callback: () => {
          if (embeddedRuntime())
            return window.open(
              `${config.baseUrl}/pins/${folderId}/${pinId}?scrollTo=true`,
              '_blank',
              'noopener noreferrer',
            )
          router.push({ name: 'pins', params: { folderId, pinId }, query: { scrollTo: 'true' } })
        },
        timeout: 8000,
      })
    }

    const close = () => {
      pinModalData.value.folderUserId = ''
      pinModalData.value.body = ''
      closePinModal()
      loading.value = false
      tags.value = []
      accountUsers.value = []
    }

    watch(
      () => route,
      () => {
        if (pinModalData.value.visible) close()
      },
    )

    watch(
      () => pinModalData.value.visible,
      async (visible) => {
        if (visible) {
          const { users } = await searchUsers({ limit: 20000 })
          accountUsers.value = users.map((user) => ({ ...user, id: String(user.id) }) as AccountUser)
        } else {
          accountUsers.value = []
        }
      },
    )

    return {
      // data
      loading,
      tags,
      accountUsers,
      pinModalData,
      // computed
      editing,
      showPreview: computed(() => {
        return pinModalData.value.referenceType !== PinReference_Type.MANUAL
      }),
      commentEditorRef,
      title: computed(() => {
        if (editing.value) return i18n.t('pins.modal.edit_pin')
        else if (pinModalData.value.referenceType === PinReference_Type.MANUAL)
          return i18n.t('pins.modal.create_new_pin')
        else if (pinModalData.value.dashboardCard) return i18n.t('pins.modal.pin_this_card')
        else if (pinModalData.value.referenceType === PinReference_Type.DASHBOARD)
          return i18n.t('pins.modal.pin_this_dashboard')
        else if (pinModalData.value.referenceType === PinReference_Type.CSAT_DASHBOARD)
          return i18n.t('pins.modal.pin_this_dashboard')
        else if (pinModalData.value.referenceType === PinReference_Type.CONVERSATION)
          return i18n.t('pins.modal.pin_this_conversation')
        else return ''
      }),
      savingDisabled,
      saveButtonText: computed(() => {
        if (editing.value) return i18n.t('pins.modal.edit_pin')
        else if (pinModalData.value.referenceType === PinReference_Type.MANUAL) return i18n.t('pins.modal.create_pin')
        else if (pinModalData.value.dashboardCard) return i18n.t('pins.modal.pin_card')
        else if (pinModalData.value.referenceType === PinReference_Type.DASHBOARD)
          return i18n.t('pins.modal.pin_dashboard')
        else if (pinModalData.value.referenceType === PinReference_Type.CSAT_DASHBOARD)
          return i18n.t('pins.modal.pin_dashboard')
        else if (pinModalData.value.referenceType === PinReference_Type.CONVERSATION)
          return i18n.t('pins.modal.pin_conversation')
        else return ''
      }),
      // methods
      close,
      async save() {
        if (savingDisabled.value) return

        await commentEditorRef.value?.prepareImagesForUploading()

        if (editing.value) {
          await saveExistingPin()
          toast({ status: 'success', message: i18n.t('pins.modal.pin_saved') })
        } else await saveNewPin()

        close()
        bus.$emit('refresh-pins-folder-content')
        bus.$emit('new-pin-saved')
      },
      userSelected(folderUserId: string) {
        pinModalData.value.folderUserId = folderUserId
      },
      setTags(event: any) {
        tags.value = event as Tag[]
      },
      TemplateType,
    }
  },
})
</script>

<style lang="scss" module>
.modal {
  --margin-vertical: 24px;

  .commentEditor {
    margin-bottom: var(--margin-vertical);
    background: var(--white);
  }

  .pinReferenceContainer {
    display: flex;
    margin-top: var(--margin-vertical);
    margin-bottom: 20px;

    .divider {
      background: var(--gray-30);
      width: 4px;
      border-radius: 2px;
      margin-right: 16px;
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    width: 100%;
  }
}
</style>
